import { useEffect, useMemo, useRef, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileUp, Loader2, FilePlus2, CheckCircle2, X } from "lucide-react";
import { DOCS_ACCENT as ACCENT, LIBRARY_META, ROOT, folderOptions, fileToDataUrl, fmtDay } from "@/lib/govDocs";

const NO_OWNER = "__none__";
const REVIEW_OPTIONS = [[0, "No scheduled review"], [6, "Every 6 months"], [12, "Every 12 months"], [24, "Every 2 years"], [36, "Every 3 years"]];
const reviewLabel = (m) => (m % 12 === 0 ? `Every ${m / 12} year${m === 12 ? "" : "s"}` : `Every ${m} months`);
const MAX_DOCX_MB = 20;

const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
  </div>
);

/**
 * Create a document (blank or imported from Word) or edit an existing one's details.
 * `doc` = existing document (edit mode) or null (create mode).
 */
export default function DocumentDetailsDialog({ open, library, doc, folders, defaultFolder, onClose, onSaved }) {
  const meta = LIBRARY_META[library];
  const creating = !doc;
  const [form, setForm] = useState(null);
  const [people, setPeople] = useState([]);
  const [imported, setImported] = useState(null);   // { filename, content_html, notes }
  const [busy, setBusy] = useState(false);
  const [codeHint, setCodeHint] = useState("");
  const suggested = useRef("");     // last code we suggested, so we only replace our own suggestion
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setImported(null);
    setForm(doc ? {
      title: doc.title, code: doc.code || "", folder: doc.folder_id || ROOT, owner: doc.owner || NO_OWNER,
      requires_ack: doc.requires_ack, review_interval_months: doc.review_interval_months || 0, review_due: doc.review_due || "",
    } : {
      title: "", code: "", folder: defaultFolder || ROOT, owner: NO_OWNER,
      requires_ack: library === "policies", review_interval_months: library === "policies" ? 12 : 0, review_due: "",
    });
    api.govPeople().then((r) => setPeople(r.people)).catch(() => setPeople([]));
    suggested.current = ""; setCodeHint("");
  }, [open, doc, library, defaultFolder]);

  // New documents: suggest the next code from the top-level folder's prefix.
  const folderVal = form?.folder;
  useEffect(() => {
    if (!open || !creating || folderVal === undefined) return;
    let live = true;
    api.govNextCode(library, folderVal === ROOT ? null : folderVal).then((r) => {
      if (!live) return;
      setForm((f) => (f && (!f.code || f.code === suggested.current) ? { ...f, code: r.code || "" } : f));
      suggested.current = r.code || "";
      setCodeHint(r.code ? `Suggested from the ${r.top_folder.name} folder.`
        : r.top_folder ? `Give the "${r.top_folder.name}" folder a code prefix to get suggested codes.` : "");
    }).catch(() => {});
    return () => { live = false; };
  }, [open, creating, library, folderVal]);

  const folderOpts = useMemo(() => folderOptions(folders), [folders]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) { toast.error("Choose a Word document (.docx)."); return; }
    if (file.size > MAX_DOCX_MB * 1024 * 1024) { toast.error(`Word documents must be under ${MAX_DOCX_MB} MB.`); return; }
    setBusy(true);
    try {
      const r = await api.govDocxToHtml(file.name, await fileToDataUrl(file));
      const det = r.details || {};
      setImported({ filename: file.name, ...r, details: det });
      setForm((f) => ({
        ...f,
        title: f.title.trim() ? f.title : file.name.replace(/\.docx$/i, "").replace(/[_]+/g, " ").trim(),
        review_interval_months: det.review_months ?? f.review_interval_months,
        owner: det.owner_match?.username || f.owner,
        import_version: det.version || "",
      }));
    } catch (err) { toast.error(getApiError(err, "Couldn't import that document.")); }
    finally { setBusy(false); }
  };

  const save = async () => {
    const extra = creating && imported ? {
      import_version: form.import_version || null,
      import_issue_date: imported.details?.issue_date || null,
      legacy_history: imported.details?.legacy_history || [],
    } : {};
    const payload = {
      ...extra,
      title: form.title, code: form.code || null, folder_id: form.folder === ROOT ? null : form.folder,
      owner: form.owner === NO_OWNER ? null : form.owner, requires_ack: form.requires_ack,
      review_interval_months: Number(form.review_interval_months), review_due: form.review_due || null,
    };
    setBusy(true);
    try {
      const saved = creating
        ? await api.govCreateDocument(library, { ...payload, content_html: imported?.content_html || "", source_filename: imported?.filename || null })
        : await api.govUpdateDocument(doc.id, payload);
      toast.success(creating ? "Draft created" : "Details saved");
      onSaved(saved);
    } catch (err) { toast.error(getApiError(err, "Couldn't save.")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-brand text-base flex items-center gap-2">
            <FilePlus2 className="h-4 w-4" style={{ color: ACCENT }} />
            {creating ? `New ${meta.singular}` : "Document details"}
          </DialogTitle>
        </DialogHeader>
        {form && (
          <div className="space-y-3 pt-1">
            {creating && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-3">
                {imported ? (
                  <div className="flex items-start gap-2 text-sm" data-testid="doc-imported">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-700 truncate">Imported {imported.filename}</div>
                      <div className="text-[11px] text-slate-500">The text will open in the editor as a draft. Check the formatting before making it Active.</div>
                      {imported.notes.map((n) => <div key={n} className="text-[11px] text-amber-700 mt-0.5">{n}</div>)}
                      <FoundInDocument d={imported.details} />
                    </div>
                    <button className="p-1 text-slate-400 hover:text-slate-600" onClick={() => setImported(null)} title="Remove import"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled={busy}
                      onClick={() => fileRef.current?.click()} data-testid="doc-import-word">
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileUp className="h-3.5 w-3.5 mr-1" />} Import from Word
                    </Button>
                    <span className="text-[11px] text-slate-500">Optional. Upload a .docx to start from its text, or leave blank to write it in the editor.</span>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={onFile} />
              </div>
            )}

            <div className="grid grid-cols-[1fr_140px] gap-3">
              <Field label="Title">
                <Input value={form.title} maxLength={200} className="h-9 text-sm" onChange={(e) => set("title", e.target.value)} data-testid="doc-title" />
              </Field>
              <Field label="Code" hint={creating ? codeHint : ""}>
                <Input value={form.code} maxLength={30} placeholder="e.g. HR-004" className="h-9 text-sm uppercase" onChange={(e) => set("code", e.target.value)} data-testid="doc-code" />
              </Field>
            </div>
            {creating && imported && (
              <Field label="Current version" hint={`The first version made Active keeps this number; later ones continue from it (e.g. ${form.import_version || "1.3"} → next minor update). Leave blank to start at 1.0.`}>
                <Input value={form.import_version} maxLength={11} placeholder="e.g. 1.3" className="h-9 text-sm w-32" data-testid="doc-import-version"
                  onChange={(e) => set("import_version", e.target.value.replace(/[^0-9.]/g, ""))} />
              </Field>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Folder">
                <Select value={form.folder} onValueChange={(v) => set("folder", v)}>
                  <SelectTrigger className="h-9 text-sm bg-white" data-testid="doc-folder"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ROOT} className="text-sm">{meta.label} (top level)</SelectItem>
                    {folderOpts.map((f) => <SelectItem key={f.id} value={f.id} className="text-sm">{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Owner">
                <Select value={form.owner} onValueChange={(v) => set("owner", v)}>
                  <SelectTrigger className="h-9 text-sm bg-white" data-testid="doc-owner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_OWNER} className="text-sm">No owner</SelectItem>
                    {people.map((p) => <SelectItem key={p.username} value={p.username} className="text-sm">{p.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Review" hint="The next review date is set automatically each time a new version goes Active.">
                <Select value={String(form.review_interval_months)} onValueChange={(v) => set("review_interval_months", Number(v))}>
                  <SelectTrigger className="h-9 text-sm bg-white" data-testid="doc-review"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REVIEW_OPTIONS.map(([v, l]) => <SelectItem key={v} value={String(v)} className="text-sm">{l}</SelectItem>)}
                    {!REVIEW_OPTIONS.some(([v]) => v === Number(form.review_interval_months)) && (
                      <SelectItem value={String(form.review_interval_months)} className="text-sm">{reviewLabel(Number(form.review_interval_months))}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              {!creating && (
                <Field label="Next review date">
                  <Input type="date" value={form.review_due} className="h-9 text-sm" onChange={(e) => set("review_due", e.target.value)} data-testid="doc-review-due" />
                </Field>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={form.requires_ack} onCheckedChange={(v) => set("requires_ack", !!v)} className="mt-0.5" data-testid="doc-ack" />
              <span>Staff must read and acknowledge each new version
                <span className="block text-[11px] text-slate-400">Usually on for policies, off for guides.</span></span>
            </label>
          </div>
        )}
        <DialogFooter className="mt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" style={{ backgroundColor: ACCENT }} onClick={save} disabled={busy || !form?.title?.trim()} data-testid="doc-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{creating ? "Create draft" : "Save details"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FoundInDocument({ d }) {
  if (!d) return null;
  const items = [];
  if (d.version) items.push(`Version ${d.version}${d.issue_date ? `, issued ${fmtDay(d.issue_date)}` : ""}`);
  if (d.owner_text) items.push(`Owner: ${d.owner_text}${d.owner_match ? ` (matched to ${d.owner_match.full_name})` : " (no match; choose the owner below)"}`);
  if (d.review_months) items.push(`Review: ${reviewLabel(d.review_months).toLowerCase()}`);
  if (d.legacy_history?.length) items.push(`${d.legacy_history.length} version history row${d.legacy_history.length === 1 ? "" : "s"}, moved to the History tab`);
  if (d.removed?.length) items.push(`Removed from the text: ${d.removed.join(" and ")}`);
  if (!items.length) return null;
  return (
    <div className="mt-2 rounded-md bg-white border border-slate-200 px-2.5 py-2" data-testid="doc-import-found">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">Found in the document</div>
      <ul className="text-[11px] text-slate-600 list-disc pl-4 space-y-0.5">{items.map((t) => <li key={t}>{t}</li>)}</ul>
      <div className="text-[10px] text-slate-400 mt-1">These have been filled in below. Check them before creating the draft.</div>
    </div>
  );
}
